import { useCallback, useEffect, useRef, useState } from "react";

import { wsUrl } from "@/game/net/wsUrl";

const PING_INTERVAL_MS = 2_000;

export type SocketStatus = "connecting" | "open" | "closed";

export type SocketPingState = {
  status: SocketStatus;
  /** Round trip browser → ASGI → channel layer → browser, in ms. */
  rttMs: number | null;
  /** Whether the server recognised our session cookie on the socket. */
  authenticated: boolean | null;
  closeCode: number | null;
};

type ServerMessage =
  | { t: "hello"; v: number; auth: boolean }
  | { t: "pong"; s: number | null; ts: number }
  | { t: "err"; code: string; msg: string };

function parseMessage(raw: unknown): ServerMessage | null {
  if (typeof raw !== "string") return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value === "object" && value !== null && "t" in value) {
      return value as ServerMessage;
    }
  } catch {
    // ignore malformed frames
  }
  return null;
}

/** Diagnostic hook for /ws/ping/ — proves the real-time path end to end. */
export function useSocketPing() {
  const [state, setState] = useState<SocketPingState>({
    status: "connecting",
    rttMs: null,
    authenticated: null,
    closeCode: null,
  });
  const [attempt, setAttempt] = useState(0);
  const sentAt = useRef(new Map<number, number>());

  useEffect(() => {
    const socket = new WebSocket(wsUrl("/ws/ping/"));
    const pending = sentAt.current;
    let seq = 0;
    let timer: number | undefined;

    setState({ status: "connecting", rttMs: null, authenticated: null, closeCode: null });

    const sendPing = () => {
      if (socket.readyState !== WebSocket.OPEN) return;
      seq += 1;
      pending.set(seq, performance.now());
      socket.send(JSON.stringify({ t: "ping", s: seq }));
    };

    socket.onmessage = (event: MessageEvent) => {
      const message = parseMessage(event.data);
      if (!message) return;

      if (message.t === "hello") {
        setState((prev) => ({ ...prev, status: "open", authenticated: message.auth }));
        sendPing();
        timer = window.setInterval(sendPing, PING_INTERVAL_MS);
      } else if (message.t === "pong" && message.s !== null) {
        const started = pending.get(message.s);
        pending.delete(message.s);
        if (started !== undefined) {
          setState((prev) => ({ ...prev, rttMs: Math.round(performance.now() - started) }));
        }
      }
    };

    socket.onclose = (event: CloseEvent) => {
      window.clearInterval(timer);
      setState((prev) => ({ ...prev, status: "closed", closeCode: event.code }));
    };

    return () => {
      window.clearInterval(timer);
      pending.clear();
      socket.onmessage = null;
      socket.onclose = null;
      socket.close();
    };
  }, [attempt]);

  const reconnect = useCallback(() => setAttempt((n) => n + 1), []);

  return { ...state, reconnect };
}